import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import 'bpmn-js/dist/assets/diagram-js.css';
import BpmnJS from 'bpmn-js/lib/Viewer';
import Canvas from 'diagram-js/lib/core/Canvas';
import React, { useEffect, useRef } from 'react';

interface CurrentProcessProps {
  xml: string;
  isExpanded?: boolean;
  showRegenerateBar?: boolean;
  onRegenerate?: () => void;
}

const BpmnViewer: React.FC<CurrentProcessProps> = ({
  xml,
  isExpanded = false,
  showRegenerateBar = false,
  onRegenerate,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const bpmnViewerRef = useRef<BpmnJS | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const centerDiagram = () => {
    if (!bpmnViewerRef.current) return;
    const canvas = bpmnViewerRef.current.get('canvas') as any;
    canvas.zoom('fit-viewport', 'auto');
  };

  // Mobile touch handling setup
  const setupMobileTouchHandling = (canvas: Canvas, container: HTMLDivElement) => {
    if (!isMobile) return () => {};

    let isScrolling = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        isScrolling = true;
        startX = touch.pageX;
        startY = touch.pageY;

        const viewbox = canvas.viewbox();
        scrollLeft = viewbox.x;
        scrollTop = viewbox.y;

        container.style.cursor = 'grabbing';
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isScrolling || e.touches.length !== 1) return;

      e.preventDefault();
      const touch = e.touches[0];
      const deltaX = (touch.pageX - startX) * 2;
      const deltaY = (touch.pageY - startY) * 2;

      const viewbox = canvas.viewbox();
      const currentZoom = canvas.zoom();

      canvas.viewbox({
        x: scrollLeft - deltaX / currentZoom,
        y: scrollTop - deltaY / currentZoom,
        width: viewbox.width,
        height: viewbox.height,
      });
    };

    const handleTouchEnd = () => {
      isScrolling = false;
      container.style.cursor = 'grab';
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  };

  const setupBpmnViewer = () => {
    const container = containerRef.current;
    if (!container || !xml) {
      console.warn('BPMN container or XML data is missing.');
      return;
    }

    if (bpmnViewerRef.current) {
      bpmnViewerRef.current.destroy();
      bpmnViewerRef.current = null;
    }

    bpmnViewerRef.current = new BpmnJS({
      container: container,
      keyboard: { bindTo: document },
    });

    const viewer = bpmnViewerRef.current;

    viewer
      .importXML(xml)
      .then(() => {
        const canvas = viewer.get('canvas') as Canvas;
        if (!canvas) {
          console.error('BPMN canvas not found.');
          return;
        }

        centerDiagram();

        let isPanning = false;
        let lastX = 0;
        let lastY = 0;

        const onMouseDown = (e: MouseEvent) => {
          if (e.button === 0 && !isMobile) {
            isPanning = true;
            lastX = e.clientX;
            lastY = e.clientY;
            container.style.cursor = 'grabbing';
            e.preventDefault();
          }
        };

        const onMouseMove = (e: MouseEvent) => {
          if (!isPanning || isMobile) return;

          const deltaX = e.clientX - lastX;
          const deltaY = e.clientY - lastY;
          lastX = e.clientX;
          lastY = e.clientY;

          const currentZoom = canvas.zoom();
          const currentViewbox = canvas.viewbox();

          canvas.viewbox({
            x: currentViewbox.x - deltaX / currentZoom,
            y: currentViewbox.y - deltaY / currentZoom,
            width: currentViewbox.width,
            height: currentViewbox.height,
          });
        };

        const onMouseUp = () => {
          isPanning = false;
          container.style.cursor = 'grab';
        };

        const onMouseLeave = () => {
          isPanning = false;
          container.style.cursor = 'grab';
        };

        const onWheel = (e: WheelEvent) => {
          e.preventDefault();
          const zoomStep = 0.1;
          const currentZoom = canvas.zoom();
          let newZoom = currentZoom;

          if (e.deltaY < 0) {
            newZoom += zoomStep;
          } else {
            newZoom -= zoomStep;
          }

          newZoom = Math.max(0.1, Math.min(newZoom, 4));

          const mouseX = e.clientX - container.getBoundingClientRect().left;
          const mouseY = e.clientY - container.getBoundingClientRect().top;

          canvas.zoom(newZoom, { x: mouseX, y: mouseY });
        };

        container.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        container.addEventListener('mouseleave', onMouseLeave);
        container.addEventListener('wheel', onWheel, { passive: false });

        const cleanupTouch = setupMobileTouchHandling(canvas, container);

        return () => {
          container.removeEventListener('mousedown', onMouseDown);
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          container.removeEventListener('mouseleave', onMouseLeave);
          container.removeEventListener('wheel', onWheel);
          cleanupTouch();
        };
      })
      .catch((err) => console.error('Error rendering BPMN diagram:', err));
  };

  useEffect(() => {
    const handleRecenter = () => centerDiagram();
    window.addEventListener('recenterDiagram', handleRecenter);
    return () => window.removeEventListener('recenterDiagram', handleRecenter);
  }, []);

  useEffect(() => {
    setupBpmnViewer();
    return () => {
      bpmnViewerRef.current?.destroy();
      bpmnViewerRef.current = null;
    };
  }, [xml, isMobile]);

  useEffect(() => {
    if (bpmnViewerRef.current && xml) {
      setTimeout(() => {
        setupBpmnViewer();
      }, 100);
    }
  }, [isExpanded, xml, isMobile]);

  return (
    // Outermost wrapper: no overflow hidden, no border — just positions children
    <Box
      sx={{
        width: '100%',
        height: isExpanded ? '100vh' : '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* BPMN Canvas — border and radius only on this box */}
      <Box
        id="expected-process-diagram"
        ref={containerRef}
        sx={{
          width: '100%',
          // Shrink canvas height when bar is visible so bar doesn't overlap
          flex: 1,
          minHeight: 0,
          backgroundColor: '#f8f8f8',
          overflow: 'hidden',
          position: 'relative',
          borderRadius: isExpanded ? 0 : '4px 4px 0 0',
          border: isExpanded ? 'none' : '1px solid #e0e0e0',
          borderBottom: showRegenerateBar ? 'none' : undefined,

          '& .djs-container': {
            backgroundColor: '#f8f8f8',
            width: '100%',
            height: '100%',
            '@media (max-width: 899px)': {
              touchAction: 'none !important',
              userSelect: 'none !important',
            },
          },
          '& .djs-palette': { display: 'none' },
          '& .djs-context-pad': { display: 'none' },
          '& .djs-canvas': {
            cursor: 'grab',
            userSelect: 'none',
            '@media (max-width: 899px)': {
              touchAction: 'none !important',
            },
          },
          '& .djs-canvas:active': { cursor: 'grabbing' },
          '& svg': {
            '@media (max-width: 899px)': {
              touchAction: 'none !important',
              pointerEvents: 'auto !important',
            },
          },
          '& canvas': {
            '@media (max-width: 899px)': {
              touchAction: 'none !important',
            },
          },
        }}
      />

      {/* Regenerate Bar — sits outside the canvas box, full width, flush bottom */}
      {showRegenerateBar && (
        <Box
          sx={{
            position: isMobile ? 'relative' : 'absolute',
            bottom: isMobile ? 'unset' : 30,

            left: isMobile ? 'unset' : '20%',
            right: isMobile ? 'unset' : '10%', // ← anchor to container edges

            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: isMobile ? 1 : 3,
            width: isMobile ? 'auto' : 'fit-content',
            m: isMobile ? 1.5 : 0,

            px: 2.5,
            py: 1,
            minHeight: 38,

            backgroundColor: '#E8EEFC',
            border: '1px solid #1A55F9',
            borderRadius: '8px',

            boxShadow: '0px 8px 24px rgba(79, 70, 229, 0.15)',
            zIndex: 5,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img src={'/images/error-blue.svg'} alt="Regenerate Diagram" height={18} width={18} />
            <Typography
              sx={{
                fontSize: isMobile ? 12 : 15,
                color: '#000',
                fontWeight: 500,
                ml: 1,
              }}
            >
              Update your diagram using the latest semantic insights.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={onRegenerate}
            sx={{
              borderColor: '#1A55F9',
              color: '#1A55F9',
              fontWeight: 500,
              fontSize: isMobile ? '12px' : '14px',
              textTransform: 'none',
              borderRadius: '4px',

              px: isMobile ? 2 : 1.5,
              minWidth: 0,
              flexShrink: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',

              backgroundColor: '#fff',

              '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                margin: 0,
              },

              '&:hover': {
                backgroundColor: '#1A55F9',
                color: '#fff',
              },
            }}
          >
            Regenerate
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BpmnViewer;
